import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Image, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons, MaterialCommunityIcons } from '../components/icons';
import { Settings, Bell, Camera, ChevronDown, User, CreditCard, MessageCircleQuestion } from '../components/icons';
import { Chip } from '../components/Chip';
import { BrandLogo } from '../components/BrandLogo';
import { Sparkle } from '../components/Sparkle';
import { BackgroundPicker } from '../components/BackgroundPicker';
import { SearchTargetSheet } from '../components/SearchTargetSheet';
import { GradientButton } from '../components/GradientButton';
import { searchStatut, DEFAULT_SEARCH_TARGET } from '../config/searchTarget';
import { COACHING_FORMULES } from '../config/coaching';
import type { SearchTarget } from '../types';
import { useProfile } from '../hooks/useProfile';
import { useGabarit } from '../hooks/useGabarit';
import { pickProfileImage } from '../services/mediaPicker';
import { uploadProfileImage } from '../services/profileImage';
import { getMonthlyStats, EMPTY_STATS, type MonthlyStats } from '../services/stats';
import { getLatestCoachingRequest, type CoachingRequest } from '../services/coaching';
import { usePaywallStore } from '../store/paywallStore';
import { ProfilLarge, type StatProfil } from './ProfilLarge';
import { initials } from '../utils/profileName';
import { PLAN_LABEL } from '../utils/planLabel';
import { formatSavedDate } from '../utils/cvDates';
import { backgroundColor } from '../theme/profileBackgrounds';
import { readableOn } from '../theme/templates';
import { colors, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';
import { fleche } from '../i18n/sens';

export interface ProfileScreenProps {
  onOpenSettings: () => void;
  // La CLOCHE ouvre la LISTE des notifications, comme celle de l'accueil. À ne pas confondre avec
  // les PRÉFÉRENCES de notification, qui restent une ligne des Réglages : deux écrans distincts.
  onOpenNotifications: () => void;
  onOpenPersonalInfo: () => void;
  onOpenCoaching: () => void;
  onOpenHelp: () => void;
}

interface StatDef {
  key: keyof MonthlyStats;
  testID: string;
  color: string; // couleur charte de l'icône
  soft: string; // pastille douce + nuance de la carte
  label: string;
  renderIcon: (color: string) => React.ReactElement;
}

// Construit AU RENDU : constante de module = libellés figés à l'import. Clés, testID, couleurs
// et icônes ne dépendent pas de la langue.
const statDefs = (): StatDef[] => [
  {
    key: 'cvCreated',
    testID: 'profile-stat-cvCreated',
    color: colors.violet,
    soft: colors.limeSoft,
    label: t('profile.stats.cvCreated'),
    renderIcon: (c) => <Ionicons name="document-text" size={22} color={c} />,
  },
  {
    key: 'interviews',
    testID: 'profile-stat-interviews',
    color: colors.electric,
    soft: colors.blueSoft,
    label: t('profile.stats.interviews'),
    renderIcon: (c) => <Ionicons name="chatbubble-ellipses" size={22} color={c} />,
  },
  {
    key: 'cvScores',
    testID: 'profile-stat-cvScores',
    color: colors.cyan,
    soft: colors.greenSoft,
    label: t('profile.stats.cvScores'),
    renderIcon: (c) => <MaterialCommunityIcons name="file-search" size={22} color={c} />,
  },
  {
    key: 'linkedinScores',
    testID: 'profile-stat-linkedinScores',
    color: colors.corail,
    soft: colors.corailSoft,
    label: t('profile.stats.linkedinScores'),
    renderIcon: (c) => <MaterialCommunityIcons name="card-account-details" size={22} color={c} />,
  },
];

// Libellé d'une demande de coaching, formulé au PASSÉ. Sans date de séance ni cycle de vie en base,
// une séance achetée il y a trois mois s'afficherait sinon comme à venir — le genre de détail qui
// fait douter du reste de l'écran.
export function coachingSummary(request: CoachingRequest | null): string {
  if (!request) return t('profile.noSession');
  // Le titre de la formule est un NOM COMMERCIAL (« Session Focus 30’ ») : il ne se traduit pas.
  const formule = COACHING_FORMULES.find((f) => f.id === request.formule)?.title ?? t('profile.sessionFallback');
  return t('profile.lastSession', { date: formatSavedDate(request.created_at), formule });
}

// Ligne d'une carte de navigation : pastille d'icône, titre, sous-titre, chevron.
function NavRow({
  icon,
  title,
  subtitle,
  tint,
  color,
  onPress,
  testID,
}: {
  icon: React.ComponentType<{ size: number; color: string; strokeWidth: number }>;
  title: string;
  subtitle: string;
  tint: string;
  color: string;
  onPress: () => void;
  testID: string;
}) {
  const Icon = icon;
  return (
    <Pressable testID={testID} accessibilityRole="button" onPress={onPress} style={styles.navRow}>
      <View style={[styles.navIcon, { backgroundColor: tint }]}>
        <Icon size={20} color={color} strokeWidth={2} />
      </View>
      <View style={styles.navTexts}>
        <Text style={styles.navTitle}>{title}</Text>
        <Text style={styles.navSubtitle}>{subtitle}</Text>
      </View>
      <Text style={styles.chevron}>{fleche('›')}</Text>
    </Pressable>
  );
}

export function ProfileScreen({
  onOpenSettings,
  onOpenNotifications,
  onOpenPersonalInfo,
  onOpenCoaching,
  onOpenHelp,
}: ProfileScreenProps) {
  const { profile, plan, save } = useProfile();
  const { large } = useGabarit();
  const [stats, setStats] = useState<MonthlyStats>(EMPTY_STATS);
  const [coaching, setCoaching] = useState<CoachingRequest | null>(null);
  const [avatarSheet, setAvatarSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [imgError, setImgError] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState(false);
  const [searchSheet, setSearchSheet] = useState(false);

  useEffect(() => {
    let active = true;
    getMonthlyStats()
      .then((s) => active && setStats(s))
      .catch(() => active && setStats(EMPTY_STATS));
    // Une lecture en échec laisse l'état vide : la section reste utilisable, le bouton de
    // réservation aussi.
    getLatestCoachingRequest()
      .then((r) => active && setCoaching(r))
      .catch(() => active && setCoaching(null));
    return () => {
      active = false;
    };
  }, []);

  const fullName =
    [profile?.first_name, profile?.last_name].map((v) => v?.trim()).filter(Boolean).join(' ') ||
    profile?.full_name?.trim() ||
    t('profile.fallbackName');
  const statut = searchStatut(profile?.search_target);
  const avatarColor = backgroundColor(profile?.avatar_key);

  const onSelectSearchTarget = async (value: SearchTarget) => {
    setSearchSheet(false);
    await save({ search_target: value });
  };

  // Choix d'une couleur : on règle la clé ET on efface la photo → retour au fond coloré.
  const onPick = async (key: string) => {
    setAvatarSheet(false);
    await save({ avatar_key: key, avatar_url: null });
  };

  // Demande d'import : on FERME d'abord le sheet, puis on lance le picker une fois la fermeture
  // terminée. Sur iOS, présenter la galerie native pendant le dismiss du <Modal> échoue en silence,
  // donc on attend onDismiss. Sur Android il n'y a pas ce conflit → direct.
  const requestImport = () => {
    setImgError(null);
    setAvatarSheet(false);
    if (Platform.OS === 'ios') setPendingImport(true);
    else void runImport();
  };

  const handleSheetDismissed = () => {
    if (!pendingImport) return;
    setPendingImport(false);
    void runImport();
  };

  const runImport = async () => {
    const res = await pickProfileImage('avatar');
    if (res.canceled) return;
    if (!res.image) {
      if (res.error) setImgError(res.error);
      return;
    }
    setBusy(true);
    try {
      const url = await uploadProfileImage('avatar', res.image.data, res.image.media_type);
      await save({ avatar_url: url });
    } catch {
      setImgError(t('profile.importFailed'));
    } finally {
      setBusy(false);
    }
  };

  const sTop = (
    <>
        <View style={styles.topRow}>
          <BrandLogo testID="profile-logo" />
          <View style={styles.topActions}>
            <Pressable
              testID="profile-notifications"
              accessibilityRole="button"
              accessibilityLabel={t('a11y.notifications')}
              onPress={onOpenNotifications}
              hitSlop={8}
              style={styles.iconBtn}
            >
              <Bell size={22} color={colors.grey700} strokeWidth={2} />
            </Pressable>
            <Pressable
              testID="profile-settings"
              accessibilityRole="button"
              accessibilityLabel={t('a11y.settings')}
              onPress={onOpenSettings}
              hitSlop={8}
              style={styles.iconBtn}
            >
              <Settings size={22} color={colors.grey700} strokeWidth={2} />
            </Pressable>
          </View>
        </View>

            </>
  );
  const sIdentite = (
    <>
{/* En-tête : avatar, identité, statut, plan. Plus de bannière — la feature est retirée. */}
        <View style={styles.headerBody}>
          <View style={styles.avatarWrap}>
            {profile?.avatar_url ? (
              <Image testID="profile-avatar-image" source={{ uri: profile.avatar_url }} style={styles.avatar} />
            ) : (
              <View style={[styles.avatar, { backgroundColor: avatarColor }]}>
                <Text style={[styles.avatarText, { color: readableOn(avatarColor) }]}>{initials(profile)}</Text>
              </View>
            )}
            {busy ? (
              <View testID="profile-image-busy" style={styles.busyOverlay}>
                <ActivityIndicator color={colors.white} />
              </View>
            ) : null}
            <Pressable
              testID="profile-avatar-btn"
              accessibilityRole="button"
              accessibilityLabel={t('a11y.changePhoto')}
              onPress={() => setAvatarSheet(true)}
              hitSlop={8}
              style={styles.cameraBtn}
            >
              <Camera size={16} color={colors.white} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={styles.nameRow}>
            <Text testID="profile-name" style={styles.name}>
              {fullName}
            </Text>
            <Chip testID="profile-plan" label={PLAN_LABEL[plan]} variant="blueSoft" />
          </View>
          <Pressable
            testID="profile-statut"
            accessibilityRole="button"
            accessibilityLabel={t('a11y.changeSearchTarget')}
            onPress={() => setSearchSheet(true)}
            hitSlop={6}
            style={styles.statutRow}
          >
            <Text style={styles.statut}>{statut}</Text>
            <ChevronDown size={16} color={colors.grey500} strokeWidth={2} />
          </Pressable>
          {imgError ? (
            <Text testID="profile-image-error" style={styles.imgError}>
              {imgError}
            </Text>
          ) : null}
        </View>

            </>
  );
  const sNav = (
    <>
{/* Carte de navigation : ces deux entrées ne figurent plus dans les Réglages. */}
        <View style={styles.block}>
          <View style={styles.card}>
            <NavRow
              icon={User}
              title={t('profile.personalInfo')}
              subtitle={t('profile.personalInfoSubtitle')}
              tint={colors.blueSoft}
              color={colors.electric}
              onPress={onOpenPersonalInfo}
              testID="profile-personal-info"
            />
            <View style={styles.divider} />
            <NavRow
              icon={CreditCard}
              title={t('profile.subscription')}
              subtitle={t('profile.subscriptionSubtitle')}
              tint={colors.limeSoft}
              color={colors.violet}
              onPress={() => usePaywallStore.getState().open()}
              testID="profile-subscription"
            />
          </View>
        </View>

            </>
  );
  const sStats = (
    <>
{/* Activité du mois — section conservée à l'identique. */}
        <View style={styles.statsBlock}>
          <Text style={styles.sectionTitle}>{t('profile.activity')}</Text>
          <View style={styles.statsGrid}>
            {statDefs().map((s) => (
              <LinearGradient
                key={s.key}
                testID={s.testID}
                colors={[colors.white, s.soft]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.statCard}
              >
                <View style={styles.statTop}>
                  <View style={[styles.statPill, { backgroundColor: s.soft }]}>{s.renderIcon(s.color)}</View>
                  <Sparkle size={14} color={s.color} style={styles.statSparkle} />
                </View>
                <Text style={styles.statValue}>{stats[s.key]}</Text>
                <Text style={styles.statLabel}>{s.label}</Text>
              </LinearGradient>
            ))}
          </View>
        </View>

            </>
  );
  const sCoaching = (
    <>
<View style={styles.block}>
          <Text style={styles.sectionTitle}>{t('profile.coaching')}</Text>
          <View testID="profile-coaching-card" style={[styles.card, styles.coachingCard]}>
            <View style={styles.coachingBody}>
              <Text testID="profile-coaching-state" style={styles.coachingState}>
                {coachingSummary(coaching)}
              </Text>
              {coaching ? (
                <Text style={styles.coachingHint}>{t('profile.coachingHint')}</Text>
              ) : null}
            </View>
            <GradientButton label={t('profile.bookSession')} onPress={onOpenCoaching} testID="profile-coaching-book" />
          </View>
        </View>

            </>
  );
  const sAide = (
    <>
<View style={styles.block}>
          <View style={styles.card}>
            <NavRow
              icon={MessageCircleQuestion}
              title={t('profile.feedbackTitle')}
              subtitle={t('profile.feedbackSubtitle')}
              tint={colors.greenSoft}
              color={colors.cyan}
              onPress={onOpenHelp}
              testID="profile-help"
            />
          </View>
        </View>
    </>
  );

  const statsLarge: StatProfil[] = statDefs().map((d) => ({
    key: d.key,
    testID: d.testID,
    color: d.color,
    label: d.label,
    value: stats[d.key],
    icon: d.renderIcon(colors.white),
  }));

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Ordinateur et tablette : une composition à part (ProfilLarge). Le téléphone garde sa pile. */}
      {large ? (
        <ProfilLarge
          nom={fullName}
          planLabel={PLAN_LABEL[plan]}
          statut={statut}
          avatarUrl={profile?.avatar_url}
          avatarColor={avatarColor}
          avatarTexteColor={readableOn(avatarColor)}
          initiales={initials(profile)}
          occupe={busy}
          erreurImage={imgError}
          stats={statsLarge}
          coachingEtat={coachingSummary(coaching)}
          coachingAvecHorairesParEmail={coaching !== null}
          onOpenNotifications={onOpenNotifications}
          onOpenSettings={onOpenSettings}
          onChangerPhoto={() => setAvatarSheet(true)}
          onChangerStatut={() => setSearchSheet(true)}
          onOpenPersonalInfo={onOpenPersonalInfo}
          onOpenAbonnement={() => usePaywallStore.getState().open()}
          onOpenCoaching={onOpenCoaching}
          onOpenHelp={onOpenHelp}
        />
      ) : (
        <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
          {sTop}
          {sIdentite}
          {sNav}
          {sStats}
          {sCoaching}
          {sAide}
        </ScrollView>
      )}

      <BackgroundPicker
        visible={avatarSheet}
        title={t('profile.avatarSheet')}
        selectedKey={profile?.avatar_key}
        onSelect={onPick}
        onImport={requestImport}
        onDismiss={handleSheetDismissed}
        onClose={() => setAvatarSheet(false)}
      />

      <SearchTargetSheet
        visible={searchSheet}
        selected={profile?.search_target ?? DEFAULT_SEARCH_TARGET}
        onSelect={onSelectSearchTarget}
        onClose={() => setSearchSheet(false)}
      />
    </SafeAreaView>
  );
}

const AVATAR = 92;

/**
 * Respiration sous le dernier bloc.
 *
 * `spacing.xxl` et non `spacing.xl` : les blocs sont séparés de `spacing.xl` (24) par leur
 * `marginTop`, et laisser exactement la même valeur en bas de page faisait lire le dernier bloc
 * comme un inter-bloc plutôt que comme la fin du contenu.
 *
 * NI la hauteur de la barre d'onglets, NI l'inset de l'appareil n'entrent ici. La barre est le
 * FRÈRE du conteneur d'écrans dans bottom-tabs v7 (`styles.screens: { flex: 1 }`), donc son espace
 * est déjà réservé ; et c'est elle qui consomme désormais l'inset bas (voir BAR_PADDING_BOTTOM).
 * Les ajouter ici les compterait deux fois.
 */
export const BOTTOM_SPACE = spacing.xxl;

/** Rembourrage intérieur des cartes du Profil, identique sur les quatre côtés. */
export const CARD_PADDING = spacing.lg;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  scroll: { paddingBottom: BOTTOM_SPACE },

  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  topActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  iconBtn: { width: 40, height: 40, borderRadius: radius.pill, alignItems: 'center', justifyContent: 'center' },

  headerBody: { paddingHorizontal: screenPadding, gap: spacing.xs },
  avatarWrap: { width: AVATAR, height: AVATAR },
  avatar: {
    width: AVATAR,
    height: AVATAR,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    borderColor: colors.white,
  },
  avatarText: { fontSize: typography.display.size, fontWeight: '700' },
  busyOverlay: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: radius.pill,
    backgroundColor: 'rgba(0,0,0,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cameraBtn: {
    position: 'absolute',
    end: -2,
    bottom: 2,
    width: 32,
    height: 32,
    borderRadius: radius.pill,
    backgroundColor: colors.electric,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.grey50,
  },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.sm, flexWrap: 'wrap' },
  name: { fontSize: typography.h1.size, fontWeight: '800', color: colors.night },
  statutRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs / 2, alignSelf: 'flex-start' },
  statut: { fontSize: typography.body.size, color: colors.grey500 },
  imgError: { fontSize: typography.small.size, color: colors.error, marginTop: spacing.xs },

  // ── Blocs de navigation ──
  block: { paddingHorizontal: screenPadding, marginTop: spacing.xl, gap: spacing.md },
  card: {
    backgroundColor: colors.white,
    borderRadius: radius.xl,
    paddingHorizontal: CARD_PADDING,
    ...shadow.card,
  },
  navRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, paddingVertical: spacing.md },
  navIcon: {
    width: 40,
    height: 40,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navTexts: { flex: 1, gap: 2 },
  navTitle: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  navSubtitle: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  chevron: { fontSize: typography.h3.size, color: colors.grey400 },
  divider: { height: 1, backgroundColor: colors.grey200 },

  // ── Coaching ──
  /**
   * Padding bas de la SEULE carte coaching.
   *
   * `styles.card` ne porte qu'un `paddingHorizontal` : son rythme vertical vient des enfants. Dans
   * les deux autres cartes du Profil, chaque `navRow` a `paddingVertical: spacing.md`, donc la
   * dernière ligne garde 12 pt sous elle. Ici le dernier enfant est le GradientButton, qui n'a rien
   * en dessous : il touchait le bord bas de la carte.
   *
   * `spacing.lg` et non `spacing.md` : c'est la valeur du `paddingHorizontal` de la carte et du
   * `paddingVertical` de `coachingBody`, donc le rembourrage devient symétrique sur les quatre
   * côtés. Style DÉDIÉ plutôt qu'ajouté à `card`, qui aurait décalé les deux autres cartes.
   */
  coachingCard: { paddingBottom: CARD_PADDING },
  coachingBody: { paddingVertical: spacing.lg, gap: spacing.xs },
  coachingState: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  coachingHint: { fontSize: typography.small.size, color: colors.grey500 },

  // ── Activité (inchangé) ──
  statsBlock: { paddingHorizontal: screenPadding, marginTop: spacing.xl, gap: spacing.md },
  sectionTitle: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  statCard: {
    width: '47%',
    flexGrow: 1,
    minHeight: 132,
    padding: spacing.lg,
    borderRadius: radius.xl,
    ...shadow.card,
  },
  statTop: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
  statPill: { width: 44, height: 44, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center' },
  statSparkle: { marginTop: spacing.xs },
  statValue: { marginTop: 'auto', paddingTop: spacing.md, fontSize: typography.display.size, fontWeight: '800', color: colors.night },
  statLabel: { fontSize: typography.small.size, color: colors.grey500 },
});
